import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { useGetCustomerQuery, useGetUserPreferencesQuery } from '@/api/v1';
import { FeatureFlagsUserContext } from '@/context/featureFlagsContext';
import { FeatureFlagsProvider } from '@/context/FeatureFlagsProvider';
import { useClarity } from '@/hooks/useClarity';
import { useLogRocket } from '@/hooks/useLogRocket';
import PrivateRoutes from '@/Routing/Private';
import PublicRoutes from '@/Routing/Public';
import { authSelector } from '@/StateManagement/AuthReducer';

export const AuthenticationGuard: React.FC = (): React.ReactElement => {
  const { pathname } = useLocation();
  const { user } = useSelector(authSelector);
  const { identify: identifyClarity } = useClarity();
  const { identify: identifyLogRocket } = useLogRocket();
  const { data: customer } = useGetCustomerQuery(undefined, { skip: !user });
  const { data: userPreferences } = useGetUserPreferencesQuery(undefined, { skip: !user });

  // Ensure we load this as soon as possible
  useGetUserPreferencesQuery(undefined, {
    skip: !user,
  });

  React.useEffect((): void => {
    if (user) {
      identifyClarity(user.id, user.sessionId, pathname, `${user.firstName} ${user.lastName}`);
      identifyLogRocket(user.id, {
        name: `${user.firstName} ${user.lastName}`,
        businessName: customer?.businessInfo?.name,
        email: user.email,
        country: customer?.businessInfo?.country,
        language: userPreferences?.language,
        timezone: userPreferences?.timezone,
        theme: userPreferences?.appearance?.theme,
        integratorId: customer?.integratorId,
        group: customer?.group,
      });
    }
  }, [
    customer,
    identifyClarity,
    identifyLogRocket,
    pathname,
    user,
    userPreferences?.appearance?.theme,
    userPreferences?.language,
    userPreferences?.timezone,
  ]);

  const ldUserContext = React.useMemo((): FeatureFlagsUserContext | undefined => {
    if (!customer?.keycloakId) {
      return undefined;
    }

    return {
      key: customer.keycloakId,
      email: customer.email,
    };
  }, [customer]);

  if (user) {
    return (
      <FeatureFlagsProvider userContext={ldUserContext}>
        <PrivateRoutes />
      </FeatureFlagsProvider>
    );
  } else {
    return <PublicRoutes />;
  }
};
